import React, { useState, useEffect, useCallback } from 'react';
import { useCustomDialog } from '../../context/DialogContext';
import { useNavigate } from 'react-router-dom';

const KitchenView = ({ token, isAdmin }) => {
    const navigate = useNavigate();
    const { alert: customAlert } = useCustomDialog();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('All'); // All, Pending, Preparing, Ready
    const [refreshKey, setRefreshKey] = useState(0);

    const fetchOrders = useCallback(async () => {
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_billing.php?action=fetch_orders', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    kitchen_status_not: 'Served'
                })
            });
            if (res.status === 401) {
                localStorage.removeItem('staff_token');
                localStorage.removeItem('staff_user');
                window.location.href = '/staff-login';
                return;
            }
            const json = await res.json();
            if (json.success) {
                const newOrders = json.orders.filter(o => {
                    if (o.kitchen_status === 'Served') return false;
                    if (o.order_type === 'Delivery' && (o.kitchen_status === 'Ready' || o.delivery_status === 'Out for Delivery' || o.delivery_status === 'Delivered')) return false;
                    return true;
                });

                // Play sound if new orders arrived
                if (newOrders.length > orders.length && orders.length > 0) {
                    const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
                    audio.play().catch(e => console.log("Sound play blocked by browser"));
                }

                setOrders(newOrders);
            }
        } catch (err) {
            console.error("Fetch Kitchen Orders Error:", err);
        } finally {
            setLoading(false);
        }
    }, [token, orders.length]);

    useEffect(() => {
        if (token) {
            fetchOrders();
            const interval = setInterval(fetchOrders, 30000); // Auto refresh every 30s
            return () => clearInterval(interval);
        }
    }, [token, fetchOrders, refreshKey]);

    const updateStatus = async (orderId, newStatus) => {
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_billing.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    action: 'update_kitchen_status',
                    order_id: orderId,
                    status: newStatus
                })
            });
            if (res.status === 401) {
                localStorage.removeItem('staff_token');
                localStorage.removeItem('staff_user');
                window.location.href = '/staff-login';
                return;
            }
            const json = await res.json();
            if (json.success) {
                setRefreshKey(prev => prev + 1);
            } else {
                await customAlert(json.message, 'Error', 'danger');
            }
        } catch (err) {
            await customAlert("Error updating status", "Error", "danger");
        }
    };

    const filteredOrders = orders.filter(o => {
        if (activeTab === 'All') return true;
        return o.kitchen_status === activeTab;
    });

    const getStatusColor = (status) => {
        switch (status) {
            case 'Pending': return '#ef4444'; // Red
            case 'Preparing': return '#f59e0b'; // Orange
            case 'Ready': return '#10b981'; // Green
            default: return '#64748b';
        }
    };

    return (
        <div style={{ padding: '20px' }}>
            {isAdmin && (
                <button 
                    onClick={() => navigate('/manage_v2_secure_auth/')} 
                    className="sticky-back-btn"
                    style={{ justifyContent: 'flex-start' }}
                >
                    <i className="fas fa-arrow-left"></i> Back to Dashboard
                </button>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', flexWrap: 'wrap', gap: '15px' }}>
                <h1 style={{ fontSize: '1.8rem', fontWeight: '800', margin: 0 }}><i className="fas fa-utensils" style={{ color: 'var(--primary-orange)', marginRight: '10px' }}></i> Kitchen Display (KOT)</h1>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {['All', 'Pending', 'Preparing', 'Ready'].map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            style={{
                                padding: '8px 20px',
                                borderRadius: '50px',
                                border: 'none',
                                background: activeTab === tab ? 'var(--primary-orange)' : '#f1f5f9',
                                color: activeTab === tab ? '#fff' : '#64748b',
                                fontWeight: '700',
                                cursor: 'pointer',
                                transition: '0.2s'
                            }}
                        >
                            {tab} {tab !== 'All' && `(${orders.filter(o => o.kitchen_status === tab).length})`}
                        </button>
                    ))}
                </div>
            </div>

            {loading && orders.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '50px', color: '#94a3b8' }}>Loading orders...</div>
            ) : filteredOrders.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))', gap: '20px' }}>
                    {filteredOrders.map(ord => (
                        <OrderTicket key={ord.id} ord={ord} onUpdate={updateStatus} getStatusColor={getStatusColor} token={token} />
                    ))}
                </div>
            ) : (
                <div style={{ textAlign: 'center', padding: '100px', background: '#f8fafc', borderRadius: '20px', border: '2px dashed #e2e8f0' }}>
                    <i className="fas fa-check-circle" style={{ fontSize: '3rem', color: '#10b981', marginBottom: '15px', display: 'block' }} />
                    <h3 style={{ margin: 0, color: '#64748b' }}>All caught up! No active orders.</h3>
                </div>
            )}
        </div>
    );
};

const OrderTicket = ({ ord, onUpdate, getStatusColor, token }) => {
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const isAwaitingPayment = ord.order_status === 'Awaiting Payment';

    useEffect(() => {
        const fetchDetails = async () => {
            try {
                const res = await fetch(`https://menu.searchisend.in/apimenu/v2/v2_billing.php?action=get_order_details&id=${ord.id}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.status === 401) {
                    localStorage.removeItem('staff_token');
                    localStorage.removeItem('staff_user');
                    window.location.href = '/kitchen-login';
                    return;
                }
                const json = await res.json();
                if (json.success) setDetails(json.order);
            } catch (err) {
                console.error("Order details error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchDetails();
    }, [ord.id, token]);

    return (
        <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', border: `2px solid ${isAwaitingPayment ? '#8b5cf6' : getStatusColor(ord.kitchen_status)}`, overflow: 'hidden', display: 'flex', flexDirection: 'column', opacity: isAwaitingPayment ? 0.75 : 1 }}>
            {/* Header */}
            <div style={{ padding: '15px 20px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <div style={{ fontWeight: '800', fontSize: '1.1rem' }}>
                        {ord.order_type === 'Delivery' ? <><i className="fas fa-motorcycle" style={{color: 'var(--primary-orange)'}}></i> Delivery Order</> : 
                         ord.order_type === 'Takeaway' ? <><i className="fas fa-shopping-bag" style={{color: 'var(--primary-orange)'}}></i> Takeaway Order</> : 
                         <><i className="fas fa-chair" style={{color: 'var(--primary-orange)'}}></i> Table: {ord.table_no}</>}
                    </div>
                    <div style={{ fontSize: '0.8em', color: '#64748b' }}>#{ord.order_no} • {new Date(ord.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
                <div style={{ padding: '4px 12px', borderRadius: '50px', background: isAwaitingPayment ? '#8b5cf6' : getStatusColor(ord.kitchen_status), color: '#fff', fontSize: '0.75em', fontWeight: '800' }}>
                    {isAwaitingPayment ? 'Awaiting Payment' : ord.kitchen_status}
                </div>
            </div>

            {/* Items */}
            <div style={{ padding: '20px', flex: 1 }}>
                {loading ? (
                    <div style={{ color: '#94a3b8', fontSize: '0.9em' }}>Loading items...</div>
                ) : details?.items?.map((item, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px dashed #f1f5f9' }}>
                        <div style={{ fontWeight: '700' }}>
                            <span style={{ display: 'inline-block', width: '30px', height: '30px', background: '#f1f5f9', borderRadius: '50%', textAlign: 'center', lineHeight: '30px', marginRight: '10px', color: 'var(--primary-orange)' }}>{item.quantity}</span>
                            {item.item_name}
                        </div>
                    </div>
                ))}
            </div>

            {/* Customer Message */}
            {ord.customer_message && (
                <div style={{ padding: '0 20px 15px', color: '#b45309' }}>
                    <div style={{ background: '#fef3c7', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #f59e0b', fontSize: '0.9em', fontWeight: '600' }}>
                        <i className="fas fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
                        {ord.customer_message}
                    </div>
                </div>
            )}

            {/* Actions */}
            <div style={{ padding: '15px 20px', background: '#f8fafc', display: 'flex', gap: '10px' }}>
                {isAwaitingPayment ? (
                    <div style={{ width: '100%', padding: '12px', borderRadius: '10px', background: '#e2e8f0', color: '#64748b', fontWeight: '700', textAlign: 'center', fontSize: '0.9em' }}>
                        <i className="fas fa-lock" style={{ marginRight: '8px' }}></i>
                        Waiting for Payment at POS
                    </div>
                ) : (
                    <>
                        {(!ord.kitchen_status || ord.kitchen_status === 'Pending') && (
                            <button onClick={() => onUpdate(ord.id, 'Preparing')} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: '#f59e0b', color: '#fff', fontWeight: '800', cursor: 'pointer' }}>Start Preparing</button>
                        )}
                        
                        {ord.kitchen_status === 'Preparing' && (
                            <button 
                                onClick={() => onUpdate(ord.id, 'Ready')}
                                style={{ flex: 1, background: '#10b981', color: '#fff', border: 'none', padding: '10px', borderRadius: '10px', fontWeight: '800', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.2)' }}>
                                <i className={ord.order_type === 'Delivery' ? "fas fa-motorcycle" : "fas fa-bell"}></i> {ord.order_type === 'Delivery' ? 'Send to Delivery' : 'Mark Ready'}
                            </button>
                        )}
                        
                        {ord.kitchen_status === 'Ready' && ord.order_type !== 'Delivery' && (
                            <button onClick={() => onUpdate(ord.id, 'Served')} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: '#6366f1', color: '#fff', fontWeight: '800', cursor: 'pointer' }}>
                                {ord.order_type === 'Takeaway' ? 'Handed to Customer' : 'Served to Table'}
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default KitchenView;
